import type { CreateBookingProps } from '@/views/bookings/booking-create/components/use-create-booking-props';
import { bookingsService } from '@/api/bookings.service';
import { useQuery } from '@tanstack/vue-query';
import type { User } from '@/entities/auth/user.entity';
import type { BookedExtraSelection } from '@/components/extras/ExtrasForm.vue';
import type {
  BookingPricingDto,
  TaxProcedure,
  UpdateCustomBookedExtraDto,
} from '@carvia/ros-client-types';
import { bookingLocationToCreateFields } from '@/utils/booking-location-to-location-dto';
import type { Local } from '@/entities/auth/local.enum';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';

const QUERY_KEY = 'create-pricing-for-booking-process';

type CreatePricingForBookingProcessParams = {
  bookedExtras: BookedExtraSelection[];
  agency?: User | null;
  customBookedExtras: UpdateCustomBookedExtraDto[] | undefined;
  taxProcedure: TaxProcedure;
  customerData?: { country?: string; vatId?: string; preferredLocal?: Local };
};

export const useCreatePricingForBookingProcess = (
  props: CreateBookingProps,
  params: MaybeRefOrGetter<CreatePricingForBookingProcessParams>,
  enabled: MaybeRefOrGetter<boolean> = true,
) =>
  useQuery({
    queryKey: computed(() => {
      const value = toValue(params);
      return [
        QUERY_KEY,
        props.selectedOffer.value?.car.id,
        props.startDate.value,
        props.endDate.value,
        props.longTerm.value,
        props.pickupLocation.value
          ? bookingLocationToCreateFields('pickup', props.pickupLocation.value)
          : null,
        props.dropoffLocation.value
          ? bookingLocationToCreateFields('dropoff', props.dropoffLocation.value)
          : null,
        props.insuranceCase.value?.id,
        value.bookedExtras,
        value.agency?.id,
        value.customBookedExtras,
        value.taxProcedure,
        value.customerData?.country,
        value.customerData?.vatId,
      ];
    }),
    queryFn: async () => {
      const value = toValue(params);
      const offer = props.selectedOffer.value!;
      const pickupLocation = props.pickupLocation.value!;
      const dropoffLocation = props.dropoffLocation.value!;
      const booking = await bookingsService.create({
        dryRun: true,
        bookedCarId: offer.car.id,
        additionalDrivers: [],
        startDate: props.startDate.value,
        endDate: props.endDate.value,
        pricing: {
          bookedExtras: value.bookedExtras,
          customBookedExtras: value.customBookedExtras,
          ...(props.longTerm.value ? { type: 'LONG_TERM' as const } : {}),
        } as BookingPricingDto,
        ...bookingLocationToCreateFields('pickup', pickupLocation),
        ...bookingLocationToCreateFields('dropoff', dropoffLocation),
        agencyId: value.agency?.id,
        insuranceCaseId: props.insuranceCase.value?.id,
        taxProcedure: value.taxProcedure,
        customerData: value.customerData,
      });
      return booking.priceCalculation;
    },
    enabled: computed(
      () =>
        toValue(enabled) &&
        !!props.selectedOffer.value &&
        !!props.pickupLocation.value &&
        !!props.dropoffLocation.value,
    ),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
